<template>
  <a-layout-header
    class="flex items-center justify-between"
    :style="{ height: '48px', padding: '0 16px', background: 'white' }"
  >
    <div class="pl-2.5 flex items-center w-full">
      <Logo />
    </div>
  </a-layout-header>
  <a-spin :spinning="spinning">
    <div class="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
      <div class="sm:mx-auto sm:w-full sm:max-w-sm">
        <div class="text-xl font-bold text-center text-gray-500">{{ $t('login') }}</div>
      </div>

      <div class="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
        <a-form class="space-y-7" :model="state" @finish="login">
          <div>
            <div class="mt-2">
              <a-form-item
                name="email"
                :rules="[{ trigger: ['blur', 'change'], validator: emailValidator }]"
              >
                <a-input
                  ref="emailInput"
                  maxlength="254"
                  :placeholder="$t('email_add')"
                  class="block w-full sm:text-sm sm:leading-6"
                  v-model:value="state.email"
                />
              </a-form-item>
            </div>
          </div>

          <div>
            <div class="mt-2">
              <a-form-item
                name="password"
                :rules="[{ required: true, message: $t('MSG_00_21'), trigger: ['blur', 'change'] }]"
              >
                <a-input-password
                  name="password"
                  maxlength="24"
                  :placeholder="$t('password')"
                  class="block w-full sm:text-sm sm:leading-6"
                  v-model:value="state.password"
                >
                </a-input-password>
              </a-form-item>
            </div>
          </div>

          <div>
            <a-button
              type="primary"
              html-type="submit"
              class="flex w-full justify-center login-but"
            >
              {{ $t('login') }}
            </a-button>
          </div>
        </a-form>

        <div class="mt-10 text-center text-sm space-y-4">
          <a @click="router.push('/reset-pass')" class="text-blue-400">{{
            $t('forgot_pass_link')
          }}</a>
          <hr class="border-t-2 border-dashed" />
          <div>
            <span class="block text-xs text-gray-400 pb-2">{{ $t('create_account_text_1') }}</span>
            <span class="block text-xs text-gray-400 mb-1">{{ $t('create_account_text_2') }}</span>
          </div>
          <a @click="router.push('/account-creation')" class="text-blue-400">{{ $t('apply') }}</a>
        </div>
      </div>
    </div>
  </a-spin>
</template>
<script setup lang="ts">
import { nextTick, onBeforeMount, onMounted, reactive, ref } from 'vue';
import Logo from '@/components/Logo.vue';
import { useRouter } from 'vue-router';
import { Input, message } from 'ant-design-vue';
import { useAuthStore, useCommonStore } from '@/stores';
import { useI18n } from 'vue-i18n';
import type { Rule } from 'ant-design-vue/es/form';
import { setApiDisabled } from '@/services/http';

const authStore = useAuthStore();
const commonStore = useCommonStore();

const spinning = ref(false);
const { t } = useI18n();
const router = useRouter();
const state = reactive({
  email: null,
  password: null
});
const emailInput = ref<typeof Input | null>(null);
onMounted(() => {
  localStorage.clear();
  nextTick(() => emailInput.value?.focus());
});
const login = async () => {
  if (state.email && state.password) {
    try {
      spinning.value = true;
      await authStore.login(state.email, state.password);
      let isAdmin = authStore.isAdmin;
      commonStore.selectedCompanyId = 'ALL';
      let redirectedFrom = router.currentRoute.value.redirectedFrom;
      if (isAdmin) {
        await router.push('/account-selection');
        if (redirectedFrom && redirectedFrom.fullPath != '/') {
          localStorage.setItem('is-redirect', 'true');
          router.currentRoute.value.redirectedFrom = redirectedFrom;
        }
      } else {
        if (redirectedFrom && redirectedFrom.fullPath != '/') {
          localStorage.setItem('is-redirect', 'true');
          await router.push(redirectedFrom.fullPath);
        } else {
          await router.push('/home');
        }
      }
    } catch (error) {
      console.error(error);
      if (error == 'Incorrect account') {
        message.error(t('MSG_01_01'));
      } else if (error == 'Inactive account') {
        message.error(t('account_is_not_enabled'));
      } else {
        message.error(t('server_error'));
      }
    } finally {
      spinning.value = false;
    }
  }
};
const emailValidator = (rule: Rule, value: string) => {
  const mailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!value) {
    return Promise.reject(new Error(t('MSG_00_21')));
  } else if (!mailRegex.test(value)) {
    return Promise.reject(new Error(t('email_invalid')));
  } else {
    return Promise.resolve();
  }
};
onBeforeMount(async () => {
  localStorage.clear();
  setApiDisabled(false);
});
</script>
<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';

.min-h-screen {
  background-color: white;
}

.login-but {
  background-color: $blue-color;
}
</style>
